import uuid from 'uuid';
import { EventEmitter } from 'events';
import ReconnectingEventSource from 'reconnecting-eventsource';
import api from 'core/util/api';
import { isDev } from 'core/util/env';
import { getApplicationMetadata, getApplicationName } from '@kentik/ui-shared/query/queryUtils';
import { generateSpoofUrl } from 'app/util/generateSpoofUrl';
import $auth from 'app/stores/$auth';

type SseOptions = {
  outType: string;
  inType: string;
  onSuccess: (data: unknown) => void;
  onReconnect?: () => void;
  onError: (error: unknown) => void;
  listenToBroadcast?: boolean;
};

// shared global sse connection
let eventSource: EventSource | undefined;
// connectionId is updated immediately on connect so outgoing requests use the correct id.
let connectionId: string | undefined;
// _postConnectId trails connectionId — it is updated after all per-client connect
// listeners have fired so that every listener sees the same isReconnect value.
let _postConnectId: string | undefined;

const HEARTBEAT_INTERVAL_MS = 60 * 1000;
// server pings every 25s (sseCluster.connect); 3 missed pings means the stream died silently
const STALE_STREAM_MS = 3 * 25 * 1000;

// eslint-disable-next-line no-use-before-define
const clients = new Set<Client>();
let heartbeatTimer: ReturnType<typeof setInterval> | undefined;
let lastEventAt = 0;

const markAlive = () => {
  lastEventAt = Date.now();
};

function openEventSource(): EventSource {
  const source = isDev ? new ReconnectingEventSource('/portal_sse/connect') : new EventSource('/portal_sse/connect');

  source.onopen = () => {};
  source.onerror = (event) => {
    console.info('sse connection error:', event.type);
  };
  source.onmessage = (event) => {
    console.info('Receive unexpected message', event.data);
  };
  source.addEventListener('connect', markAlive);
  source.addEventListener('ping', markAlive);
  markAlive();
  return source;
}

function heartbeat() {
  if (eventSource?.readyState !== EventSource.OPEN) {
    return;
  }
  if (Date.now() - lastEventAt > STALE_STREAM_MS) {
    eventSource.close();
    eventSource = openEventSource();
    clients.forEach((client) => client.attach());
    return;
  }
  api
    .post(`/portal_sse/heartbeat?connectionId=${connectionId}`, { noRedirect: true, showErrorToast: false })
    .catch(() => {});
}

export class Client extends EventEmitter {
  private _channelId: string;

  private _opt: SseOptions;

  private _abortController: AbortController;

  constructor(options: SseOptions) {
    super();
    if (!eventSource) {
      eventSource = openEventSource();
    }
    if (!heartbeatTimer) {
      heartbeatTimer = setInterval(heartbeat, HEARTBEAT_INTERVAL_MS);
    }

    this._opt = options;
    this._channelId = uuid.v4();
    this._abortController = new AbortController();
    this.on('reconnect', this.onReconnect);

    clients.add(this);
    this.attach();
  }

  attach() {
    eventSource?.addEventListener('connect', this.connectHandleFn, { signal: this._abortController.signal });
    eventSource?.addEventListener(this.getInChannel(), this.channelHandleFn, { signal: this._abortController.signal });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private connectHandleFn = (event: any) => {
    const welcomeMessage = JSON.parse(event.data);
    // Compare against _postConnectId (the previous cycle's value) so every
    // listener computes isReconnect correctly — _postConnectId is only
    // advanced after all listeners for this event have run.
    const isReconnect = _postConnectId !== undefined && _postConnectId !== welcomeMessage.connectionId;
    // Update connectionId immediately so that send() calls inside reconnect handlers
    // post to the correct (new) endpoint right away.
    connectionId = welcomeMessage.connectionId;
    // Defer advancing _postConnectId until the current synchronous dispatch
    // of this event has finished. All listeners receive the same new id, so the
    // repeated assignment is idempotent.
    setTimeout(() => {
      _postConnectId = welcomeMessage.connectionId;
    }, 0);
    if (isReconnect) {
      this.emit('reconnect');
    } else {
      this.emit('connect');
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private channelHandleFn = (event: any) => {
    let data;
    try {
      data = JSON.parse(event.data);
    } catch (_err) {
      console.warn('error parsing sse json data:', event.data);
      data = event.data;
    }
    if (data && typeof data === 'object' && 'error' in data) {
      setTimeout(() => this.onError(data), 10);
    } else {
      setTimeout(() => this.onSuccess(data), 10);
    }
  };

  send(payload: object): void {
    if (!this.isOpen) {
      console.info(`Unable to send data with sse status ${this.readyStateText}. Retry after connection.`);
      if (this.isNew) {
        this.once('connect', () => this.send(payload));
      } else {
        this.once('reconnect', () => this.send(payload));
      }
    } else {
      let newPath = window.location.pathname;
      if (newPath.includes('/export')) {
        newPath = newPath.replace('/export', '');
      }

      payload = Object.assign({ requestID: this._channelId }, payload, {
        responseChannel: this.getInChannel(),
        spoofURL: generateSpoofUrl($auth),
        source: getApplicationName(newPath),
        application_metadata: getApplicationMetadata(payload || payload || {}, newPath)
      });
      api.post(this.getOutChannel(), { body: payload });
    }
  }

  get readyStateText(): string {
    switch (eventSource?.readyState) {
      case EventSource.CONNECTING:
        return 'CONNECTING (0)';
      case EventSource.OPEN:
        return 'OPEN (1)';
      case EventSource.CLOSED:
        return 'CLOSED (2)';
      default:
        return `UNKNOWN (${eventSource?.readyState})`;
    }
  }

  get isOpen(): boolean {
    return eventSource?.readyState === EventSource.OPEN;
  }

  get isNew(): boolean {
    return connectionId === undefined;
  }

  getInChannel() {
    if (this._opt.listenToBroadcast) {
      return this._opt.inType;
    }
    return `${this._opt.inType}/${this._channelId}`;
  }

  getOutChannel() {
    return `/portal_sse/${this._opt.outType}?connectionId=${connectionId}`;
  }

  // Clean up this client's listeners without affecting the shared eventSource
  cleanup() {
    clients.delete(this);
    // clean up eventSource listeners('connect' and channel subscription)
    this._abortController.abort();
    // clean up this object's listener
    this.off('reconnect', this.onReconnect);
  }

  // probably should not use since eventSource is globally shared
  destroy() {
    this.cleanup();
    if (eventSource && eventSource?.readyState !== EventSource.CLOSED) {
      eventSource.close();
      eventSource = undefined;
      connectionId = undefined;
      _postConnectId = undefined;
      clearInterval(heartbeatTimer);
      heartbeatTimer = undefined;
    }
  }

  onSuccess = (data: unknown) => {
    if (!this._opt.onSuccess) {
      this.emit('onSuccess', data);
    } else {
      this._opt.onSuccess(data);
    }
  };

  onReconnect = () => {
    if (this._opt.onReconnect) {
      this._opt.onReconnect();
    }
  };

  onError = (error: unknown) => {
    if (!this._opt.onError) {
      this.emit('onError', error);
    } else {
      this._opt.onError(error);
    }
  };
}
